import React, { useEffect, useState } from 'react';
import { ArrowLeft, ExternalLink, Loader2, LogOut, Video } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAgentAuth } from '../../contexts/AgentAuthContext';
import { classEvents } from '../../utils/api';
import TodayCard from './TodayCard';

const PowerUpQuickEntry = () => {
  const { agent, logout } = useAgentAuth();
  const [powerUp, setPowerUp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    const loadPowerUp = async () => {
      try {
        const response = await classEvents.agent.getTodayPowerUp();
        if (active) setPowerUp(response.data?.data || null);
      } catch (requestError) {
        if (active) {
          setError(requestError.response?.data?.message || 'Could not load the Power Up link.');
        }
      } finally {
        if (active) setLoading(false);
      }
    };
    loadPowerUp();
    return () => { active = false; };
  }, []);

  return (
    <main id="power-up-quick-entry-page" className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 px-4 py-5 sm:py-8">
      <section id="power-up-quick-entry-shell" className="mx-auto w-full max-w-xl">
        <header id="power-up-quick-entry-header" className="mb-5 flex items-center justify-between gap-4">
          <section id="power-up-quick-entry-identity" className="min-w-0">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-blue-600">Productivity Coaching</p>
            <h1 className="truncate text-2xl font-black text-gray-950">Good morning, {agent?.firstName || 'Agent'}</h1>
          </section>
          <button
            id="power-up-quick-entry-logout"
            type="button"
            onClick={logout}
            className="inline-flex min-h-11 flex-shrink-0 items-center rounded-xl border border-gray-300 bg-white px-3 text-sm font-semibold text-gray-700"
          >
            <LogOut className="mr-2 h-4 w-4" /> Sign out
          </button>
        </header>

        <section id="power-up-join-card" className="mb-5 rounded-2xl border border-emerald-200 bg-white p-5 shadow-sm">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-emerald-700">Today’s Power Up</p>
          <h2 className="mt-1 text-2xl font-black text-gray-950">Join the Zoom meeting</h2>
          {powerUp?.startTime && (
            <p id="power-up-start-time" className="mt-1 text-sm font-medium text-gray-600">Starts at {powerUp.startTime}</p>
          )}
          {loading ? (
            <p id="power-up-link-loading" role="status" className="mt-5 flex min-h-14 items-center justify-center rounded-xl bg-gray-100 text-sm font-semibold text-gray-600">
              <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading meeting link…
            </p>
          ) : powerUp?.meetingLink ? (
            <a
              id="join-power-up-link"
              href={powerUp.meetingLink}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-5 inline-flex min-h-16 w-full items-center justify-center rounded-xl bg-emerald-600 px-5 py-4 text-xl font-black text-white shadow-sm hover:bg-emerald-700"
            >
              <Video className="mr-3 h-6 w-6" /> Join Power Up <ExternalLink className="ml-2 h-5 w-5" />
            </a>
          ) : (
            <aside id="power-up-link-missing" className="mt-5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-900">
              {error || 'The Zoom link has not been posted yet. You can still report your numbers below.'}
            </aside>
          )}
          <p className="mt-3 text-sm text-gray-600">Zoom opens separately, so this page stays ready for your numbers.</p>
        </section>

        <TodayCard />

        <Link id="power-up-back-to-dashboard" to="/agent/dashboard" className="mt-5 inline-flex min-h-11 items-center text-sm font-bold text-blue-700">
          <ArrowLeft className="mr-2 h-4 w-4" /> Go to my full dashboard
        </Link>
      </section>
    </main>
  );
};

export default PowerUpQuickEntry;
